/**
 * Audit Item Actions Component
 *
 * Handles the action buttons section (skip, like/dislike, add/edit decision).
 * Extracted from AuditItem.tsx to reduce complexity and improve maintainability.
 */

import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { IconButton, Edit, Add, IconSize, OverlayMode } from '@soteria/design-system-ve';

import { LikeIconButton } from '../../LikeIconButton/LikeIconButton';
import * as styles from '../AuditItem.css';

import { SkipButton } from './SkipButton';

interface AuditItemActionsProps {
  item: GraphQLAuditItem;
  currentUserDecision?: GraphQLAuditDecision;
  onSkipToggle?: (itemId: string, isSkipped: boolean) => void;
  onOpenDecisionPanel: (e: React.MouseEvent) => void;
  onLikeButtonLoadingChange: (loading: boolean) => void;
  isSessionCompleted?: boolean;
}

export function AuditItemActions({
  item,
  currentUserDecision,
  onSkipToggle,
  onOpenDecisionPanel,
  onLikeButtonLoadingChange,
  isSessionCompleted = false,
}: AuditItemActionsProps) {
  return (
    <div className={styles.decisionsActions}>
      {/* Skip Button - Always show but disable when session is completed */}
      <SkipButton isSessionCompleted={isSessionCompleted} item={item} onSkipToggle={onSkipToggle} />

      {/* Like/Dislike buttons - Hide when session is completed */}
      {!isSessionCompleted && (
        <LikeIconButton currentUserDecision={currentUserDecision} item={item} sessionId={item.auditSessionId} onLoadingChange={onLikeButtonLoadingChange} />
      )}

      {/* Add/Edit Decision Button - Hide when session is completed */}
      {!isSessionCompleted && (
        <IconButton
          icon={currentUserDecision ? Edit : Add}
          iconSize={IconSize.XSmall}
          overlayMode={OverlayMode.Solid}
          title={currentUserDecision ? 'Edit decision' : 'Add decision'}
          onClick={onOpenDecisionPanel}
        />
      )}
    </div>
  );
}
